import { Component, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BotGuild } from 'src/app/models/bot/guild';
import { GuildService } from 'src/app/services/guild.service';
import { Location, ViewportScroller } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { faBars, faPlus, faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Subscription, filter, take } from 'rxjs';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { BotOwnerPreviewService, BotOwnerViewAs } from 'src/app/services/bot-owner-preview.service';

interface AdsPlacement {
  name: string;
  description: string;
  image: string;
  url: string;
}

@Component({
  selector: 'app-guild-dashboard',
  templateUrl: './guild-dashboard.component.html',
  styleUrls: ['./guild-dashboard.component.css']
})
export class GuildDashboardComponent implements OnInit, OnDestroy {
  faIcon = {
    menubar: faBars,
    add: faPlus,
    arrowLeft: faArrowLeft,
    times: faTimes,
  };

  /** Same breakpoint as the dashboard sidebar layout. */
  private readonly overlayQuery = '(max-width: 720px)';

  /** Overlay drawer on narrow viewports; persistent column otherwise. */
  @HostBinding('class.nav-overlay') isOverlay = false;
  /** Open state for the overlay drawer. Ignored while the sidebar is persistent. */
  mobileOpen = false;

  channelSelected = false;
  signupTemplateSelected = false;
  repeatingEventSelected = false;
  signupGroupSelected = false;
  isWebsiteBotOwner = false;
  selectedViewAs: BotOwnerViewAs = 'actual';
  viewAsOptions: { value: BotOwnerViewAs; label: string }[] = [
    { value: 'actual', label: 'Your permissions (edit)' },
    { value: 'owner_admin', label: 'Owner/Admin' },
    { value: 'moderator', label: 'Moderator' },
    { value: 'organizer', label: 'Organizer' },
    { value: 'user', label: 'Normal user' },
  ];
  private routerSubscription: Subscription | undefined;
  private breakpointSubscription: Subscription | undefined;

  ads_nr = 0;

  ads: AdsPlacement[] = [
    {
      name: 'Proton Mail',
      description: 'Proton is a safe and privacy first swiss email, vpn, calendar, password manager and drive service.<br><br>This is also where I host my emails and it\'s the vpn of my choice!',
      image: '/assets/images/proton_mail.png',
      url: 'https://go.getproton.me/SHg6'
    },
    {
      name: 'Proton VPN',
      description: 'Proton is a safe and privacy first swiss email, vpn, calendar, password manager and drive service.<br><br>This is also where I host my emails and it\'s the vpn of my choice!',
      image: '/assets/images/proton_vpn.png',
      url: 'https://go.getproton.me/SHg8'
    },
    // {
    //   name: 'Proton Drive',
    //   description: 'Proton is a safe and privacy first swiss email, vpn, calendar, password manager and drive service.<br><br>This is also where I host my emails and it\'s the vpn of my choice!',
    //   image: '/assets/images/proton_drive.svg',
    //   url: 'https://go.getproton.me/SHg9'
    // },
    {
      name: 'Hetzner',
      description: 'Hetzner is a German hosting company that offers a wide range of services.<br>I use their very reliable VPS for all my projects (including Raid Organizer) and their Cloud Storage services.<br><br>Click the banner to get 20€ worth of credits.',
      image: '/assets/images/hetzner.svg',
      url: 'https://hetzner.cloud/?ref=eJn45HQkudQl'
    },
    {
      name: 'UptimeRobot',
      description: 'UptimeRobot is a free service that monitors your services every 5 minutes and notifies you if your service is down.<br>I use it to monitor all my projects, including Raid Organizer.',
      image: '/assets/images/uptimerobot.svg',
      url: 'https://uptimerobot.com/?rid=82613b2c2b76a4'
    },
    // {
    //   name: 'Netlify',
    //   description: 'Netlify is a hosting service for static websites. This service makes it very easy to host your website.<br>I use it to host all Raid Organizer related websites.',
    //   image: '',
    //   url: ''
    // },
    {
      name: 'Hover',
      description: 'Hover is a domain name registrar. All my domains (including <a href="https://raidorganizer.org" target="_blank">raidorganizer.org</a>) are registered there.<br><br>Get 2$ off your first purchase and support me by clicking the banner.',
      image: '/assets/images/hover.svg',
      url: 'https://hover.com/fxcG33B9'
    },
    {
      name: 'Humblebundle',
      description: 'Humblebundle is a charity website where you can buy game, book and software bundles for a very cheap price. They work together with publishers to 1) offer bundles cheaper and 2) support a charity with those earnings.<br><br>I buy a lot of my IT books there.',
      image: '/assets/images/humblebundle.svg',
      url: 'https://www.humblebundle.com/?partner=snawe'
    },
  ]


  guild: BotGuild | undefined;
  raidOrganizerGuild: BotGuild | undefined;
  id: string;
  showDescription: boolean = false;
  onImage: boolean = false;
  onDescription: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    public guildService: GuildService,
    private observer: BreakpointObserver,
    public router: Router,
    private viewportScroller: ViewportScroller,
    private botResourceService: BotResourceService,
    private botOwnerPreviewService: BotOwnerPreviewService,
  ) { }

  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id');
    this.breakpointSubscription = this.observer.observe([this.overlayQuery]).subscribe(state => {
      if (state.matches === this.isOverlay) {
        return;
      }
      this.isOverlay = state.matches;
      this.mobileOpen = false;
    });
    this.selectedViewAs = this.botOwnerPreviewService.getViewAs();
    this.botResourceService.getSession().pipe(take(1)).subscribe(
      session => {
        this.isWebsiteBotOwner = session.is_website_bot_owner;
        this.botOwnerPreviewService.setWebsiteBotOwner(this.isWebsiteBotOwner);
        this.loadGuild();
      },
      () => {
        this.botOwnerPreviewService.setWebsiteBotOwner(false);
        this.loadGuild();
      }
    );
    this.raidOrganizerGuild = this.guildService.raidOrganizerGuild;
    this.ads_nr = Math.floor(Math.random() * this.ads.length);
    this.updateSectionState();

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSectionState();
        this.mobileOpen = false;
        this.viewportScroller.scrollToPosition([0, 0]);
      });
  }

  onSidenavOpenedChange(opened: boolean) {
    if (this.isOverlay) {
      this.mobileOpen = opened;
    }
  }

  toggleMobileNav() {
    this.mobileOpen = !this.mobileOpen;
  }

  loadGuild() {
    this.guildService.getGuildData(this.id).subscribe(guild => {
      this.guild = guild;
    });
  }

  setViewAs(value: BotOwnerViewAs) {
    this.selectedViewAs = value;
    this.botOwnerPreviewService.setViewAs(value);
    this.guildService.clearGuildData();
    this.loadGuild();
  }

  getViewAsLabel(value: string | null | undefined = this.selectedViewAs): string {
    return this.viewAsOptions.find(option => option.value === value)?.label || 'Owner/Admin';
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }

  private updateSectionState() {
    const url = this.router.url;
    this.channelSelected = url.includes('channel/');
    this.signupTemplateSelected = url.includes('signup-template/');
    this.repeatingEventSelected = url.includes('repeating-event/');
    this.signupGroupSelected = url.includes('signup-group/');
  }

  isOverviewRoute(): boolean {
    if (!this.id) {
      return false;
    }
    
    // Check the current child route path segments (most reliable method)
    const childRoute = this.route.snapshot.firstChild;
    
    if (childRoute) {
      // Get the path segments of the child route
      const childPath = childRoute.url.map(segment => segment.path).join('/');
      // Since we redirect empty path to overview, just check for 'overview'
      return childPath === 'overview';
    }
    
    // Fallback: check URL directly (normalize by removing query params and trailing slashes)
    const url = this.router.url.split('?')[0].replace(/\/$/, '');
    const basePath = `/guilds/${this.id}`;
    const normalizedBase = basePath.replace(/\/$/, '');
    
    // Check if we're on overview path (empty path will redirect, so shouldn't need to check base)
    return url === `${normalizedBase}/overview` || url === normalizedBase;
  }

  isPremiumRoute(): boolean {
    if (!this.id) {
      return false;
    }
    
    // Check URL directly (most reliable for route changes)
    const url = this.router.url.split('?')[0].replace(/\/$/, '');
    const basePath = `/guilds/${this.id}`;
    const normalizedBase = basePath.replace(/\/$/, '');
    
    return url === `${normalizedBase}/premium`;
  }

  hideDescription() {
    if (this.onDescription || this.onImage) {
      return;
    }
    this.showDescription = false;
  }

  enterImage() {
    this.onImage = true;
    this.showDescription = true;
  }

  enterDescription() {
    this.onDescription = true;
    this.showDescription = true;
  }

  leaveDescription() {
    setTimeout(() => {
      this.onDescription = false;
      this.hideDescription();
    }, 200);
  }

  leaveImage() {
    setTimeout(() => {
      this.onImage = false;
      this.hideDescription();
    }, 200);
  }

  goBack(): void {
    this.location.back();
  }
}
